import { Dialog } from 'base/ui/dialog';
import { Rows } from 'base/ui/layout';
import { DividerLine } from 'base/ui/panel';
import {
  TextRegularLight,
  TextSmallLight,
  TextTinyLight,
} from 'base/ui/typography';
import classNames from 'classnames';
import React from 'react';
import styles from './keyboard_shortcuts_dialog.css';
import { isMac } from './platform';

export function KeyboardShortcutsDialog({ onClose }: { onClose: () => void }) {
  return (
    <Dialog title="Keyboard shortcuts" onClose={onClose} width="wide">
      <div className={styles.columns}>
        <Group label="Edit">
          <AlignShortcuts>
            <Shortcut label="Copy" shortcut="⌘C" />
            <Shortcut label="Cut" shortcut="⌘X" />
            <Shortcut label="Paste" shortcut="⌘V" />
            <Shortcut label="Duplicate" shortcut="⌘D" />
          </AlignShortcuts>
          <Shortcut label="Delete" shortcut="⌫" />
          <Shortcut label="Undo" shortcut="⌘Z" />
          <Shortcut label="Redo" shortcut="⇧⌘Z" />
          <Shortcut label="Select child" shortcut="Enter" />
          <Shortcut label="Select parent" shortcut="\" />
        </Group>
        <Group label="View">
          <AlignShortcuts>
            <Shortcut label="Zoom in" shortcut="⌘+,Z + click" />
            <Shortcut label="Zoom out" shortcut="⌘-,⌥Z + click" />
            <Shortcut label="Zoom to fit" shortcut="⌘1" />
          </AlignShortcuts>
          <AlignShortcuts>
            <Shortcut label="Show edit panel" shortcut="⌥8" />
            <Shortcut label="Show add panel" shortcut="⌥A" />
            <Shortcut label="Toggle comments" shortcut="⌥C" />
            <Shortcut label="Sitemap view" shortcut="⌥S" />
            <Shortcut label="Wireframe view" shortcut="⌥W" />
          </AlignShortcuts>
          <AlignShortcuts>
            <Shortcut label="Desktop breakpoint" shortcut="1" />
            <Shortcut label="Tablet breakpoint" shortcut="2" />
            <Shortcut label="Mobile breakpoint" shortcut="3" />
          </AlignShortcuts>
        </Group>
        <Rows spacing={32}>
          <Group label="Layout">
            <Shortcut label="Add new page" shortcut="↩" />
            <Shortcut label="Add new section" shortcut="↩" />
            <Shortcut label="Move section up" shortcut="↑" />
            <Shortcut label="Move section down" shortcut="↓" />
          </Group>
          <Group label="Tools">
            <Shortcut label="Add comment" shortcut="C" />
            <Shortcut label="Exit commenting" shortcut="Esc, V" />
          </Group>
        </Rows>
      </div>
    </Dialog>
  );
}

function Group({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <Rows spacing={12}>
      <TextRegularLight color="grey">{label}</TextRegularLight>
      <DividerLine />
      {children}
    </Rows>
  );
}

function AlignShortcuts({ children }: { children: React.ReactNode }) {
  return <div className={styles.shortcutsGrid}>{children}</div>;
}

export function Shortcut({
  label,
  shortcut,
  color = 'grey',
  size = 'small',
}: {
  label: string;
  shortcut: string;
  color?: 'grey' | 'black';
  size?: 'small' | 'tiny';
}) {
  // Swap shortcut modifiers to match the OS
  if (!isMac) {
    shortcut = shortcut.replace('⌘', 'Ctrl');
  }

  const shortcutOptions = shortcut.split(',');
  const renderedShortcuts = [];

  for (let i = 0; i < shortcutOptions.length; i++) {
    const shortcutText = shortcutOptions[i];
    renderedShortcuts.push(
      <div className={classNames(styles.shortcut, styles[`color-${color}`])}>
        <TextTinyLight>{shortcutText}</TextTinyLight>
      </div>,
    );
    if (i !== shortcutOptions.length - 1) {
      renderedShortcuts.push(<TextTinyLight color="grey">or</TextTinyLight>);
    }
  }

  const Text = size === 'small' ? TextSmallLight : TextTinyLight;

  return (
    <div className={styles.shortcutRow}>
      <Text>{label}</Text>
      <div className={styles.shortcutOptionsContainer}>{renderedShortcuts}</div>
    </div>
  );
}
